import { useEffect, useMemo, useState } from "react";
import DatePickerInput from "../../../../components/DatePickerInput";
import { Button, Dialog, Field, Input, Notice, Select, Textarea, plainError } from "../../../../components/ui";
import { formatDay } from "../myHrText";
import { structureChanges } from "../employeeView";

const AUTHORITY = { 100: "Business administrator", 85: "General or store manager", 60: "Department manager", 40: "Officer or specialist", 20: "Staff" };
export const authorityLabel = (level) => (level ? AUTHORITY[Number(level)] || `Level ${level}` : null);

const formFrom = (member) => ({
  departmentId: member?.departmentId ? String(member.departmentId) : "",
  businessRoleId: member?.businessRoleId || member?.roleId ? String(member.businessRoleId || member.roleId) : "",
  supervisorMembershipId: member?.supervisorMembershipId ? String(member.supervisorMembershipId) : "",
  employmentType: member?.employmentType || "",
  hireDate: member?.hireDate ? String(member.hireDate).slice(0, 10) : "",
  contactNumber: member?.contactNumber || "",
  employmentEmail: member?.employmentEmail || member?.email || "",
  remarks: member?.remarks || "",
});

const CONSEQUENCE = {
  businessRoleId: "Their access changes to what the new role allows, from their next request.",
  departmentId: "They move to the new department's lists and reports.",
  supervisorMembershipId: "Their requests go to the new supervisor.",
  employmentType: "Their employment record shows the new type.",
  hireDate: "Payroll and leave count their employment from the new start date.",
};

/**
 * Edit employment — department, role (which is also their access), supervisor, type, start date and contact
 * details. Contact details and remarks save directly; any change that affects access, reporting lines or
 * payroll coverage is reviewed first, field by field (DESIGN.md › When not to add a second confirmation).
 *
 * `canChangeRole` (owner decision D-1, APP-RBAC-004): an existing employee's role is their access, and only the
 * business owner changes it. For anyone else the role is shown, not editable, and is not sent; the server refuses
 * a role change from them whatever the form sends.
 */
export function employmentPayload(form, { canChangeRole, selectedRole }) {
  return {
    departmentId: form.departmentId || null,
    ...(canChangeRole ? {
      businessRoleId: form.businessRoleId || null,
      hierarchyLevel: selectedRole?.hierarchy_level ? Number(selectedRole.hierarchy_level) : null,
    } : {}),
    supervisorMembershipId: form.supervisorMembershipId || null,
    employmentType: form.employmentType || null,
    hireDate: form.hireDate || null,
    contactNumber: form.contactNumber.trim() || null,
    employmentEmail: form.employmentEmail.trim() || null,
    remarks: form.remarks.trim() || null,
  };
}

/**
 * `adding` (APP-HR-110): the person is a business member with no employment record yet. Saving creates one, which is
 * the one deliberate way they become an employee, so the start date is required and the dialog says what changes and
 * what does not.
 */
export default function EmploymentStructureModal({ open, member, departments, roles, members, employmentTypes, canChangeRole = true, adding = false, onClose, onSubmit }) {
  const [form, setForm] = useState(() => formFrom(member));
  const [step, setStep] = useState("edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [problems, setProblems] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(formFrom(member));
    setStep("edit");
    setSaving(false);
    setError("");
    setProblems({});
  }, [open, member]);

  const rolesInDepartment = useMemo(
    () => (form.departmentId ? roles.filter((role) => String(role.departmentId || role.department_id || "") === form.departmentId) : []),
    [form.departmentId, roles],
  );
  const selectedRole = rolesInDepartment.find((role) => String(role.id) === form.businessRoleId) || null;
  // A role belongs to a department, so someone who cannot change the role cannot move its holder out of it either.
  const currentRole = roles.find((role) => String(role.id) === form.businessRoleId) || null;
  const departmentLocked = !canChangeRole && Boolean(currentRole?.departmentId || currentRole?.department_id);
  const supervisors = useMemo(
    () => members.filter((candidate) => String(candidate.id) !== String(member?.id)
      && !["INVITED", "TERMINATED", "LEFT"].includes(String(candidate.status || "").toUpperCase())
      && !String(candidate.id).startsWith("inv-")),
    [member?.id, members],
  );

  const set = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (canChangeRole && key === "departmentId" && !roles.some((role) => String(role.id) === current.businessRoleId && String(role.departmentId || role.department_id || "") === value)) {
        next.businessRoleId = "";
      }
      return next;
    });
  };

  const names = (key, value) => {
    if (value === "" || value == null) return "";
    if (key === "departmentId") return departments.find((d) => String(d.id) === String(value))?.name || "Unknown department";
    if (key === "businessRoleId") {
      const role = roles.find((r) => String(r.id) === String(value));
      return role ? role.displayName || role.display_name || role.name : member?.roleDisplayName || member?.businessRoleName || "Current role";
    }
    if (key === "supervisorMembershipId") return members.find((m) => String(m.id) === String(value))?.name || member?.supervisorName || "Unknown";
    if (key === "employmentType") return employmentTypes.find((t) => t.value === value)?.label || value;
    if (key === "hireDate") return formatDay(value, { year: true });
    return value;
  };
  const changes = structureChanges(formFrom(member), form, names);
  const reviewNeeded = changes.some((change) => change.consequential);

  const validate = () => {
    const next = {};
    if (form.employmentEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.employmentEmail.trim())) next.employmentEmail = "Enter a valid email address.";
    // An employment record states when the person started; payroll and leave count from it. The server refuses a
    // record without one, and a start date inside a payroll that is already with Finance or paid.
    if (!form.hireDate) next.hireDate = adding ? "Enter the date they started." : (member?.hireDate ? "A start date cannot be removed. Change it to the correct date." : "");
    if (!next.hireDate) delete next.hireDate;
    setProblems(next);
    return !Object.keys(next).length;
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await onSubmit(employmentPayload(form, { canChangeRole, selectedRole }));
    } catch (err) {
      setError(plainError(err, "The changes were not saved. Try again."));
      setSaving(false);
    }
  };

  const next = (event) => {
    event.preventDefault();
    if (saving || !changes.length || !validate()) return;
    if (reviewNeeded) setStep("review");
    else save();
  };

  if (!open || !member) return null;
  const name = member.name || "this employee";

  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}
      title={step === "review"
        ? (adding ? `Make ${name} an employee?` : `Save changes to ${name}'s employment?`)
        : (adding ? `Add an employment record for ${name}` : `Edit ${name}'s employment`)}
      description={step === "review"
        ? (adding ? "They become an employee from the start date. Their access to the business does not change." : "Check what changes. The rest of the record stays as it is.")
        : (adding
          ? "This makes them an employee of the business. It does not change their access, and it records no pay."
          : (canChangeRole
            ? "Department, role, supervisor, type and start date are reviewed before saving."
            : "Supervisor, type and start date are reviewed before saving."))}
      footer={step === "review" ? (
        <>
          <Button onClick={() => setStep("edit")} disabled={saving}>Back to editing</Button>
          <Button variant="primary" loading={saving} onClick={save}>{adding ? "Add employment record" : "Save changes"}</Button>
        </>
      ) : (
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" form="employment-record-form" loading={saving} disabled={!changes.length}>
            {reviewNeeded ? (adding ? "Review" : "Review changes") : (adding ? "Add employment record" : "Save changes")}
          </Button>
        </>
      )}
    >
      {step === "review" ? (
        <div className="space-y-3 text-body-sm">
          <dl className="divide-y divide-subtle border-y border-subtle">
            {changes.map((change) => (
              <div key={change.key} className="grid gap-1 py-2 sm:grid-cols-[10rem_minmax(0,1fr)]">
                <dt className="text-secondary">{change.label}</dt>
                <dd className="text-foreground">
                  <span className="text-secondary">{change.from}</span>
                  <span aria-hidden="true" className="px-1.5 text-tertiary">→</span>
                  <span className="sr-only"> changes to </span>
                  <span className="font-medium">{change.to}</span>
                </dd>
              </div>
            ))}
          </dl>
          <ul className="list-disc space-y-1 pl-5 text-secondary">
            {changes.filter((change) => CONSEQUENCE[change.key]).map((change) => <li key={change.key}>{CONSEQUENCE[change.key]}</li>)}
          </ul>
          {error ? <Notice tone="danger" title="Not saved" role="alert">{error}</Notice> : null}
        </div>
      ) : (
        <form id="employment-record-form" noValidate onSubmit={next} className="grid gap-4 sm:grid-cols-2">
          <Field label="Department" hint={departmentLocked ? "Comes with their role." : undefined}>
            <Select value={form.departmentId} onChange={set("departmentId")} disabled={departmentLocked}>
              <option value="">Not assigned</option>
              {departments.map((department) => <option key={department.id} value={String(department.id)}>{department.name}</option>)}
            </Select>
          </Field>
          <Field
            label="Role"
            hint={!canChangeRole
              ? "Their role is also their access. Only the business owner can change it."
              : (form.departmentId ? `Also sets their access${selectedRole?.hierarchy_level ? ` · ${authorityLabel(selectedRole.hierarchy_level)}` : ""}.` : "Choose a department first.")}
          >
            <Select value={form.businessRoleId} onChange={set("businessRoleId")} disabled={!canChangeRole || !form.departmentId}>
              <option value="">{form.departmentId ? "Choose a role" : "Not assigned"}</option>
              {form.businessRoleId && !selectedRole ? <option value={form.businessRoleId}>{names("businessRoleId", form.businessRoleId)}</option> : null}
              {rolesInDepartment.map((role) => <option key={role.id} value={String(role.id)}>{role.displayName || role.display_name || role.name}</option>)}
            </Select>
          </Field>
          <Field label="Supervisor">
            <Select value={form.supervisorMembershipId} onChange={set("supervisorMembershipId")}>
              <option value="">Not assigned</option>
              {supervisors.map((candidate) => <option key={candidate.id} value={String(candidate.id)}>{candidate.name}</option>)}
            </Select>
          </Field>
          <Field label="Employment type">
            <Select value={form.employmentType} onChange={set("employmentType")}>
              <option value="">Not assigned</option>
              {employmentTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </Select>
          </Field>
          <Field
            label="Start date"
            required={adding}
            error={problems.hireDate}
            hint={adding ? "Required. Pay and payroll count from this date." : "The first day they worked here."}
          >
            <DatePickerInput value={form.hireDate} onValueChange={set("hireDate")} title="Select start date" ariaLabel="Start date" />
          </Field>
          <Field label="Contact number">
            <Input value={form.contactNumber} onChange={set("contactNumber")} maxLength={50} inputMode="tel" />
          </Field>
          <Field label="Work email" error={problems.employmentEmail} className="sm:col-span-2">
            <Input type="email" value={form.employmentEmail} onChange={set("employmentEmail")} maxLength={255} />
          </Field>
          <Field label="Remarks" hint="Kept on the employment record." className="sm:col-span-2">
            <Textarea value={form.remarks} onChange={set("remarks")} rows={3} maxLength={1000} />
          </Field>
          {!member.employeeCode && (adding || member.onboardingApplicable !== false) ? (
            <p className="text-meta text-tertiary sm:col-span-2">Saving gives them an employee code.</p>
          ) : null}
          {error ? <Notice tone="danger" title="Not saved" role="alert" className="sm:col-span-2">{error}</Notice> : null}
        </form>
      )}
    </Dialog>
  );
}
